import React, { useRef, useCallback, useState } from 'react'
import { Portal } from 'react-portal'
import { positionRight } from '@reach/popover'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import * as yup from 'yup'

import {
  InvitePeopleField,
  InvitePeopleFieldForm,
  InvitePeopleFieldEmailField,
  InvitePeopleContent,
  InvitePeopleFieldInviteButton,
} from '@sketch/modules-common'
import { PrivatePermissionsAccessSelect } from 'modules/shares/components/AccessLevelSelect'
import { ShareForSettingsModal } from '../types'
import {
  AddShareMembershipMutationVariables,
  WorkspaceMembershipFragment,
  useGetInvitableEmailUsersQuery,
  InvitableUserEmailFragment,
} from '@sketch/gql-types'
import { useAddShareMembership } from 'modules/shares/operations/useAddShareMembership/useAddShareMembership'
import { useDebounceValue } from '@sketch/utils'
import { ConfirmationDialog } from '@sketch/components'
import { NonMemberError } from './InvitePeople.styles'
import { useAutocomplete } from './useAutocomplete'

type Values = Pick<
  AddShareMembershipMutationVariables['input'],
  'guestCommentsEnabled' | 'guestInspectEnabled' | 'privateAccessLevel'
> & {
  shouldValidateEmail: boolean
  email: string
  userIdentifier: string | null
  role: WorkspaceMembershipFragment['role']
}

const INITIAL_VALUES: Values = {
  shouldValidateEmail: true,
  email: '',
  guestCommentsEnabled: true,
  guestInspectEnabled: true,
  privateAccessLevel: 'VIEW',
  userIdentifier: null,
  role: 'GUEST',
}

const VALIDATION_SCHEMA = yup.object().shape({
  shouldValidateEmail: yup.boolean(),
  email: yup.string().max(255).required().when('shouldValidateEmail', {
    is: true,
    then: yup.string().trim().lowercase().email(),
  }),
  userIdentifier: yup.string().nullable(),
})

export interface ShareForInvite {
  share: Pick<
    ShareForSettingsModal,
    | 'identifier'
    | 'publicUrl'
    | 'publicAccessLevel'
    | 'publicCommentsEnabled'
    | 'userCanUpdateCommentsEnabled'
    | 'type'
    | 'name'
    | 'workspace'
  >
}

interface InvitePeopleProps extends ShareForInvite {
  hideLabel?: boolean
  filterMembers?: 'Guests' | 'Members'
}

interface FormikRenderProps {
  formikBag: FormikProps<typeof INITIAL_VALUES>
  shareIdentifier: string
  workspaceIdentifier: string
  loading: boolean
  disabled: boolean
  hideLabel?: boolean
  filterMembers?: 'Guests' | 'Members'
}

const FormikRender = ({
  formikBag,
  shareIdentifier,
  workspaceIdentifier,
  loading,
  disabled,
  hideLabel,
  filterMembers,
}: FormikRenderProps) => {
  const containerRef = useRef<HTMLInputElement>(null)
  const autocomplete = useAutocomplete({
    shareIdentifier,
    workspaceIdentifier,
  })
  const { isValid, values, setFieldValue, errors } = formikBag

  const accessLevelSelectPositioning = (targetRect: any, popoverRect: any) => {
    const inputRect = containerRef.current?.getBoundingClientRect()

    if (!inputRect) {
      // Align it to the target rect if no ref is provided
      return positionRight(targetRect, popoverRect)
    }

    const position = positionRight(inputRect, popoverRect)
    const topPosition = position.top as string
    // Subtract 5px to top positioning so that it is aligned
    // to the bottom edge of the input
    position.top = `${parseFloat(topPosition.split('px')[0]) - 5}px`

    return position
  }

  const showError =
    filterMembers &&
    ['Is not an inviteable member of this workspace'].includes(
      errors.email ?? ''
    )

  return (
    <>
      <InvitePeopleFieldForm>
        <InvitePeopleFieldEmailField name="email">
          <InvitePeopleField
            label={hideLabel ? '' : 'Manage Access'}
            placeholder="Add a name or email address"
            autocomplete={autocomplete}
            containerRef={containerRef}
            dynamicAction={props => {
              const workspaceMembership = props.selectedItem as Partial<WorkspaceMembershipFragment>
              // We need to check that the membership is not null because when not having any
              // selected user we want to make the checkboxes editable
              const isNotGuest =
                workspaceMembership !== null &&
                workspaceMembership?.role !== 'GUEST'

              return (
                <PrivatePermissionsAccessSelect
                  level={values.privateAccessLevel!}
                  setLevel={level => {
                    setFieldValue('privateAccessLevel', level)

                    if (level === 'EDIT') {
                      setFieldValue('guestCommentsEnabled', true)
                      setFieldValue('guestInspectEnabled', true)
                    }
                  }}
                  setCommentsEnabled={event =>
                    setFieldValue(
                      'guestCommentsEnabled',
                      event.target.checked ?? false
                    )
                  }
                  setInspectEnabled={event =>
                    setFieldValue(
                      'guestInspectEnabled',
                      event.target.checked ?? false
                    )
                  }
                  commentsEnabled={
                    values.guestCommentsEnabled ||
                    values.privateAccessLevel === 'EDIT' ||
                    isNotGuest
                  }
                  inspectEnabled={
                    values.guestInspectEnabled ||
                    values.privateAccessLevel === 'EDIT' ||
                    isNotGuest
                  }
                  disabled={values.privateAccessLevel === 'EDIT' || isNotGuest}
                  showDisabledText={isNotGuest}
                  hideCommentLabel
                  position={accessLevelSelectPositioning}
                />
              )
            }}
          />
        </InvitePeopleFieldEmailField>
        <InvitePeopleFieldInviteButton
          size="32"
          type="submit"
          loading={loading}
          disabled={disabled || !isValid}
        >
          Add
        </InvitePeopleFieldInviteButton>
      </InvitePeopleFieldForm>
      {showError && <NonMemberError>{errors.email}</NonMemberError>}
    </>
  )
}

const InvitePeople = ({
  share,
  hideLabel,
  filterMembers,
}: InvitePeopleProps) => {
  const {
    userIdentifier: addedUserIdentifier,
    addMember,
    addMembershipLoading,
    getAddMemberConfirmDialogProps,
    addMemberConfirmDialogOpen,
  } = useAddShareMembership()

  const [inviteableUsers, setInviteableUsers] = useState<
    InvitableUserEmailFragment[] | null
  >(null)
  const [searchEmail, setSearchEmail] = useState<string | null>(null)
  const debouncedSearchValue = useDebounceValue(searchEmail, 300)

  const { loading: invitableUsersLoading } = useGetInvitableEmailUsersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      shareIdentifier: share.identifier,
      workspaceIdentifier: share.workspace.identifier,
      search: debouncedSearchValue,
    },
    skip: !debouncedSearchValue || filterMembers === undefined,
    onCompleted(data) {
      setInviteableUsers(data?.invitableUsers.entries ?? null)
    },
  })

  const loading = addMembershipLoading || invitableUsersLoading
  const confirmDialogOpen = addMemberConfirmDialogOpen
  const getConfirmDialogProps = (userIdentifier: string | undefined) =>
    getAddMemberConfirmDialogProps()

  const handleSubmit = useCallback(
    (values: Values, actions: FormikHelpers<Values>) => {
      actions.resetForm()

      const variables = {
        shareIdentifier: share.identifier,
        privateAccessLevel: values.privateAccessLevel,
        guestCommentsEnabled: values.guestCommentsEnabled,
        guestInspectEnabled: values.guestInspectEnabled,
        userIdentifier: values.userIdentifier || undefined,
        email: values.email || undefined,
      }

      addMember({
        input: variables,
      })
    },
    [share, addMember]
  )

  const handleValidateEmail = ({ email }: { email: string }) => {
    setSearchEmail(email)

    const errors: Partial<Record<keyof Values, string>> = {}

    if (filterMembers === undefined || !email) {
      return
    }

    // this extra condition needs to be here for cases like ines@omg.l, which is still not a valid email
    // but no worth checking against the member emails yet
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      errors.email = 'Invalid email'

      return errors
    }

    // check if email is among the ones on the workspace
    const isMember = inviteableUsers?.some(
      member => 'user' in member && member.user?.email === email
    )

    if (!isMember && filterMembers === 'Members') {
      errors.email = 'Is not an inviteable member of this workspace'
    }

    return errors
  }

  return (
    <>
      <InvitePeopleContent data-testid="invite-people-form">
        <Formik<Values>
          validationSchema={VALIDATION_SCHEMA}
          validateOnBlur={false}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmit}
          validate={handleValidateEmail}
        >
          {formikBag => (
            <FormikRender
              formikBag={formikBag}
              shareIdentifier={share.identifier}
              workspaceIdentifier={share.workspace.identifier}
              loading={loading}
              disabled={!debouncedSearchValue}
              hideLabel={hideLabel}
              filterMembers={filterMembers}
            />
          )}
        </Formik>
      </InvitePeopleContent>
      {confirmDialogOpen && (
        <Portal>
          <ConfirmationDialog
            {...getConfirmDialogProps(addedUserIdentifier!)}
            title={'Add yourself to this document?'}
          >
            You will no longer be able to edit this document. If you need Edit
            permissions again later, ask a Workspace Admin.
          </ConfirmationDialog>
        </Portal>
      )}
    </>
  )
}

export default InvitePeople
